import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import NextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Colors } from "../styles/Colors";
import { useNavigate } from "react-router-dom";

export const navBottomButtonSx = {
  textTransform: "none",
  borderRadius: 2,
  px: 2.25,
  py: 1,
  fontWeight: 600,
  minHeight: 40,
  boxShadow: "none",
  "&:hover": { boxShadow: 1 },
};

/**
 * @param {"bar" | "embedded"} variant — bar: full-width strip; embedded: centered actions for use inside main content
 */
const NavBarBottom = ({
  prevPage,
  nextPage,
  enableNextPage,
  nextPageClickFunc,
  variant = "bar",
  returnLabel = "Return to list",
  /** If set, replaces the Return button (e.g. width page "Back" that opens revert dialog). */
  leftSlot = null,
  /** When variant is embedded: optional node on the left (e.g. Cancel); Back/Next stay on the right. */
  leadingSlot = null,
  /** When true, omit the Return/Back button on the right (use with leadingSlot for Cancel-only + Next). */
  hideReturnButton = false,
  /** Shows a spinner beside Next and disables Next while an async step runs (e.g. upload + measure width). */
  nextLoading = false,
  /** While nextLoading, also disables the Return/back button so the user does not leave mid-request. */
  backDisabledWhenNextLoading = false,
  /** When true, omit the Next button entirely. */
  hideNextButton = false,
  /** Optional custom label for the Next button (e.g. "Upload next batch (2 of 5)"). */
  nextLabel = "Next",
}) => {
  const navigate = useNavigate();
  const embedded = variant === "embedded";

  const onReturn = () => navigate(prevPage);

  const returnButtonEl =
    hideReturnButton ? null : leftSlot != null ? (
      leftSlot
    ) : (
      <Button
        variant="contained"
        disableElevation
        startIcon={<ArrowBackIcon />}
        sx={navBottomButtonSx}
        onClick={onReturn}
        disabled={Boolean(backDisabledWhenNextLoading && nextLoading)}
      >
        {returnLabel}
      </Button>
    );

  const nextEl = hideNextButton ? null : (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
      <Button
        variant="contained"
        disableElevation
        disabled={enableNextPage || Boolean(nextLoading)}
        endIcon={<NextIcon />}
        sx={navBottomButtonSx}
        onClick={() => nextPageClickFunc(nextPage)}
      >
        {nextLabel}
      </Button>
      {nextLoading ? (
        <CircularProgress size={24} thickness={4} aria-label="Working" />
      ) : null}
    </Stack>
  );

  if (embedded) {
    if (leadingSlot != null) {
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          sx={{ width: "100%", alignSelf: "stretch", pt: 2, gap: 1.5 }}
        >
          <Box sx={{ flexShrink: 0 }}>{leadingSlot}</Box>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            flexWrap="wrap"
            sx={{ gap: 1.5 }}
          >
            {returnButtonEl}
            {nextEl}
          </Stack>
        </Stack>
      );
    }
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        sx={{ width: "100%", alignSelf: "stretch", pt: 2, gap: 1.5 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>{returnButtonEl}</Box>
        {nextEl}
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      alignSelf="stretch"
      flexWrap="wrap"
      sx={{
        backgroundColor: Colors.navbarBottomBackground,
        minHeight: 72,
        width: "100%",
        flexShrink: 0,
        boxSizing: "border-box",
        px: 2,
        py: 1.5,
        gap: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>{returnButtonEl}</Box>
      {nextEl}
    </Stack>
  );
};

export default NavBarBottom;
